import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useCompanies, useLogout, useMe } from '../api/hooks';
import type { Company } from '../api/types';
import { Btn } from '../components/ui';
import { plural } from '../lib/format';
import { useCompanyFilter } from '../state/company';

/**
 * Výběr firmy po přihlášení, jako účetní jednotka v POHODĚ. Bez zvolené firmy
 * se do aplikace nevstoupí – firmy mají oddělené doklady, kontakty i platby.
 */

/** Iniciály pro značku firmy: dvě první písmena slov, u jednoslovné dvě z názvu. */
function initials(name: string): string {
  const words = name
    .replace(/\b(s\.r\.o\.|a\.s\.|z\.s\.|s\. r\. o\.)\b/gi, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

function address(s: Company): string {
  return [s.street, [s.zip, s.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
}

export function SelectCompany() {
  const { data: me, isPending: mePending } = useMe();
  const { data: companies = [], isPending } = useCompanies();
  const { setCompanyId, remember, setRemember } = useCompanyFilter();
  const logout = useLogout();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (!mePending && !me) return <Navigate to="/prihlaseni" replace />;

  const enter = (id: string) => {
    setBusy(true);
    setCompanyId(id);
    navigate('/', { replace: true });
  };

  return (
    <div className="pick">
      <div className="pick-box">
        <div className="pick-head">
          <div>
            <div className="pick-app">iSupply Faktury</div>
            <h1>Vyberte firmu</h1>
            <p>
              Každá firma má vlastní doklady, kontakty, platby a číselné řady. Nic se mezi nimi
              nemíchá.
            </p>
          </div>
          <Btn onClick={() => enterNew(navigate, setCompanyId, companies)}>Přidat firmu</Btn>
        </div>

        <section className="pick-list">
          <div className="pick-th">
            <span>Firma</span>
            <span>IČO</span>
            <span>DPH</span>
            <span className="num">Doklady</span>
          </div>

          {companies.map((s) => (
            <button
              type="button"
              key={s.id}
              className="pick-row"
              onClick={() => enter(s.id)}
              disabled={busy}
            >
              <span className="pick-name">
                <span className="pick-mark" style={{ background: s.accentColor }}>
                  {initials(s.name)}
                </span>
                <span>
                  <span className="pick-title">{s.name}</span>
                  <span className="pick-addr">{address(s) || 'Adresa zatím nevyplněná'}</span>
                </span>
              </span>
              <span className="pick-ico">{s.ico ?? '—'}</span>
              <span>
                <span className={`pill ${s.vatPayer ? 'paid' : 'cancelled'}`}>
                  {s.vatPayer ? 'Plátce DPH' : 'Neplátce'}
                </span>
              </span>
              <span className="pick-count">
                {s.documentCount}
                <span className="muted only-m"> dokl.</span>
              </span>
            </button>
          ))}

          {!companies.length && (
            <div className="empty">
              {isPending ? 'Načítám firmy…' : 'Zatím tu není žádná firma. Založ první.'}
            </div>
          )}

          <button
            type="button"
            className="pick-row pick-new"
            onClick={() => enterNew(navigate, setCompanyId, companies)}
          >
            <span className="pick-name">
              <span className="pick-mark pick-plus">+</span>
              <span className="pick-title">Nová firma — stačí zadat IČO, zbytek doplní ARES</span>
            </span>
            <span />
            <span />
            <span />
          </button>
        </section>

        <div className="pick-foot">
          <label className="pick-remember">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />
            <span>Příště otevřít rovnou naposledy použitou firmu</span>
          </label>
          <span className="pick-links">
            {companies.length > 1 && (
              <button type="button" className="link" onClick={() => enter('all')}>
                Zobrazit všechny firmy dohromady →
              </button>
            )}
            <button
              type="button"
              className="link"
              onClick={() => logout.mutate(undefined, { onSuccess: () => navigate('/prihlaseni') })}
            >
              Odhlásit se
            </button>
          </span>
        </div>

        <p className="hint">
          {companies.length} {plural(companies.length, ['firma', 'firmy', 'firem'])} na účtu
          {me?.email ? ` · ${me.email}` : ''}
        </p>
      </div>
    </div>
  );
}

/**
 * Novou firmu zakládá obrazovka Moje firmy. Aby do ní bylo kam vejít, vstoupí se
 * do první existující firmy; když žádná není, do režimu „Všechny firmy".
 */
function enterNew(
  navigate: (to: string, opts?: { replace?: boolean }) => void,
  setCompanyId: (id: string) => void,
  companies: Company[],
) {
  setCompanyId(companies[0]?.id ?? 'all');
  navigate('/vice/firmy?nova=1', { replace: true });
}
